import * as THREE from 'three';
import type { RoadSegment } from './OpenWorldMapLoader';

export type StreetFurnitureOptions = {
  maxLights: number;
  maxSignals: number;
};

export class StreetFurnitureSystem {
  private group = new THREE.Group();

  private lightPoles!: THREE.InstancedMesh;
  private lightHeads!: THREE.InstancedMesh;

  private signalPoles!: THREE.InstancedMesh;
  private signalHeads!: THREE.InstancedMesh;

  private lightCount = 0;
  private signalCount = 0;

  private tmpMat = new THREE.Matrix4();
  private tmpPos = new THREE.Vector3();
  private tmpQuat = new THREE.Quaternion();
  private tmpScale = new THREE.Vector3(1, 1, 1);

  private lampHeadMat!: THREE.MeshStandardMaterial;
  private signalHeadMat!: THREE.MeshStandardMaterial;

  constructor(private scene: THREE.Scene, private opts: StreetFurnitureOptions) {
    this.group.name = 'StreetFurniture';
    this.scene.add(this.group);

    // Streetlight geometry
    const poleGeo = new THREE.CylinderGeometry(0.06, 0.08, 6.0, 10);
    const poleMat = new THREE.MeshStandardMaterial({ color: 0x2a2c33, roughness: 0.7, metalness: 0.2 });

    const headGeo = new THREE.SphereGeometry(0.18, 10, 10);
    this.lampHeadMat = new THREE.MeshStandardMaterial({
      color: 0x202028,
      roughness: 0.2,
      metalness: 0.2,
      emissive: new THREE.Color(0xffffff),
      emissiveIntensity: 0.0
    });

    this.lightPoles = new THREE.InstancedMesh(poleGeo, poleMat, opts.maxLights);
    this.lightHeads = new THREE.InstancedMesh(headGeo, this.lampHeadMat, opts.maxLights);
    this.lightPoles.castShadow = true;
    this.lightHeads.castShadow = false;

    this.group.add(this.lightPoles);
    this.group.add(this.lightHeads);

    // Traffic signal geometry
    const sigPoleGeo = new THREE.CylinderGeometry(0.05, 0.06, 4.2, 10);
    const sigPoleMat = new THREE.MeshStandardMaterial({ color: 0x23252c, roughness: 0.75, metalness: 0.25 });

    const sigHeadGeo = new THREE.BoxGeometry(0.3, 0.8, 0.22);
    this.signalHeadMat = new THREE.MeshStandardMaterial({
      color: 0x0d0f14,
      roughness: 0.55,
      metalness: 0.15,
      emissive: new THREE.Color(0xff2b2b),
      emissiveIntensity: 0.0
    });

    this.signalPoles = new THREE.InstancedMesh(sigPoleGeo, sigPoleMat, opts.maxSignals);
    this.signalHeads = new THREE.InstancedMesh(sigHeadGeo, this.signalHeadMat, opts.maxSignals);
    this.group.add(this.signalPoles);
    this.group.add(this.signalHeads);
  }

  dispose() {
    this.scene.remove(this.group);
    this.group.traverse((o) => {
      const m = (o as any).material as THREE.Material | THREE.Material[] | undefined;
      const g = (o as any).geometry as THREE.BufferGeometry | undefined;
      if (Array.isArray(m)) m.forEach(mm => mm.dispose());
      else m?.dispose();
      g?.dispose();
    });
  }

  reset() {
    this.lightCount = 0;
    this.signalCount = 0;
    this.lightPoles.count = 0;
    this.lightHeads.count = 0;
    this.signalPoles.count = 0;
    this.signalHeads.count = 0;
  }

  /**
   * Call this per tile load, pass the roads of that tile
   */
  addFromRoads(roads: RoadSegment[]) {
    for (const r of roads) {
      // spacing based on road type
      const spacing =
        r.type === 'motorway' ? 38 :
        r.type === 'primary' ? 28 :
        r.type === 'secondary' ? 24 :
        r.type === 'residential' ? 18 :
        22;

      // add streetlights along the road
      this.placeAlongPolyline(r.points, spacing, (pos, dir, idx) => {
        // avoid overfill
        if (this.lightCount >= this.opts.maxLights) return;

        const sideOffset = (idx % 2 === 0 ? 1 : -1) * (r.width * 0.55 + 1.2);
        const right = new THREE.Vector3(dir.z, 0, -dir.x);
        const p = pos.clone().addScaledVector(right, sideOffset);

        this.addStreetLight(p, dir);
      });

      // traffic lights only for city-ish roads sometimes
      if (r.type === 'primary' || r.type === 'secondary' || r.type === 'residential') {
        // 1 signal every ~400m
        this.placeAlongPolyline(r.points, 420, (pos, dir) => {
          if (this.signalCount >= this.opts.maxSignals) return;
          const right = new THREE.Vector3(dir.z, 0, -dir.x);
          const p = pos.clone().addScaledVector(right, r.width * 0.55 + 0.8);
          this.addTrafficSignal(p, dir);
        });
      }
    }

    this.lightPoles.count = this.lightCount;
    this.lightHeads.count = this.lightCount;
    this.signalPoles.count = this.signalCount;
    this.signalHeads.count = this.signalCount;

    this.lightPoles.instanceMatrix.needsUpdate = true;
    this.lightHeads.instanceMatrix.needsUpdate = true;
    this.signalPoles.instanceMatrix.needsUpdate = true;
    this.signalHeads.instanceMatrix.needsUpdate = true;
  }

  setNightFactor(night: number) {
    // night: 0 day -> 1 night
    this.lampHeadMat.emissiveIntensity = 0.15 + night * 2.2;
    this.signalHeadMat.emissiveIntensity = 0.10 + night * 1.5;
  }

  /**
   * optionally animate signals (red/green)
   */
  update(dt: number) {
    const t = performance.now() * 0.001;
    const phase = (Math.sin(t * 0.6) + 1) * 0.5; // 0..1
    // red -> green tint
    const col = new THREE.Color().setHSL(phase * 0.33, 1, 0.5);
    this.signalHeadMat.emissive.copy(col);
  }

  private addStreetLight(pos: THREE.Vector3, dir: THREE.Vector3) {
    const yaw = Math.atan2(dir.x, dir.z);
    this.tmpQuat.setFromAxisAngle(new THREE.Vector3(0, 1, 0), yaw);

    // Pole
    this.tmpPos.set(pos.x, 3.0, pos.z);
    this.tmpMat.compose(this.tmpPos, this.tmpQuat, this.tmpScale);
    this.lightPoles.setMatrixAt(this.lightCount, this.tmpMat);

    // Head
    this.tmpPos.set(pos.x, 5.9, pos.z);
    this.tmpMat.compose(this.tmpPos, this.tmpQuat, this.tmpScale);
    this.lightHeads.setMatrixAt(this.lightCount, this.tmpMat);

    this.lightCount++;
  }

  private addTrafficSignal(pos: THREE.Vector3, dir: THREE.Vector3) {
    const yaw = Math.atan2(dir.x, dir.z);
    this.tmpQuat.setFromAxisAngle(new THREE.Vector3(0, 1, 0), yaw);

    // Pole
    this.tmpPos.set(pos.x, 2.1, pos.z);
    this.tmpMat.compose(this.tmpPos, this.tmpQuat, this.tmpScale);
    this.signalPoles.setMatrixAt(this.signalCount, this.tmpMat);

    // Head
    this.tmpPos.set(pos.x + dir.x * 0.4, 3.4, pos.z + dir.z * 0.4);
    this.tmpMat.compose(this.tmpPos, this.tmpQuat, this.tmpScale);
    this.signalHeads.setMatrixAt(this.signalCount, this.tmpMat);

    this.signalCount++;
  }

  private placeAlongPolyline(
    pts: THREE.Vector3[],
    spacing: number,
    cb: (pos: THREE.Vector3, dir: THREE.Vector3, idx: number) => void
  ) {
    if (pts.length < 2) return;
    let carry = 0;
    let idx = 0;

    for (let i = 1; i < pts.length; i++) {
      const a = pts[i - 1];
      const b = pts[i];
      const seg = b.clone().sub(a);
      const len = seg.length();
      if (len < 1e-3) continue;
      const dir = seg.clone().multiplyScalar(1 / len);

      let t = spacing - carry;
      while (t < len) {
        const p = a.clone().addScaledVector(dir, t);
        cb(p, dir, idx++);
        t += spacing;
      }
      carry = Math.max(0, t - len);
    }
  }
}
